import { isExpired, type Session, type TokenStore } from '@stayon/auth-core';
import { createLogoutFinality, type LogoutFinality } from './logoutFinality';

type SessionMessage =
  | { type: 'session'; session: Session; epoch: number }
  | { type: 'logout'; epoch: number };

interface MessageChannel {
  onmessage: ((event: MessageEvent<SessionMessage>) => void) | null;
  postMessage(message: SessionMessage): void;
  close(): void;
}

interface LockManager {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

interface RefreshCoordinatorOptions {
  tokenStore: TokenStore;
  channel?: MessageChannel | null;
  locks?: LockManager | null;
  logoutFinality?: LogoutFinality;
  onRemoteLogout?: () => void;
  onRemoteSession?: () => void;
}

export interface RefreshCoordinator {
  runRefresh(attempt: () => Promise<Session>): Promise<Session | null>;
  runLogout<T>(attempt: () => Promise<T>): Promise<T>;
  publishLogin(session: Session): void;
  hasFinalLogout(): boolean;
  close(): void;
}

const channelName = 'stayon-auth-session';
const lockName = 'stayon-auth-session-mutation';

function defaultChannel(): MessageChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(channelName);
}

function defaultLocks(): LockManager | null {
  return typeof navigator === 'undefined' || !navigator.locks ? null : navigator.locks;
}

function isSession(value: unknown): value is Session {
  if (!value || typeof value !== 'object') return false;
  const session = value as Partial<Session>;
  return (
    typeof session.accessToken === 'string' &&
    typeof session.accessExpiresAt === 'string' &&
    typeof session.csrfToken === 'string' &&
    session.tokenType === 'Bearer'
  );
}

export function createRefreshCoordinator({
  tokenStore,
  channel = defaultChannel(),
  locks = defaultLocks(),
  logoutFinality = createLogoutFinality(),
  onRemoteLogout,
  onRemoteSession,
}: RefreshCoordinatorOptions): RefreshCoordinator {
  let revision = 0;

  const publish = (session: Session, epoch: number) => {
    tokenStore.set(session);
    revision += 1;
    channel?.postMessage({ type: 'session', session, epoch });
  };

  if (channel) {
    channel.onmessage = (event) => {
      if (!Number.isFinite(event.data?.epoch)) return;
      const current = logoutFinality.read();
      if (event.data?.type === 'logout') {
        if (event.data.epoch < current.epoch) return;
        logoutFinality.adopt({ epoch: event.data.epoch, loggedOut: true });
        tokenStore.clear();
        onRemoteLogout?.();
      } else if (event.data?.type === 'session' && isSession(event.data.session)) {
        if (
          event.data.epoch < current.epoch ||
          (event.data.epoch === current.epoch && current.loggedOut)
        )
          return;
        logoutFinality.adopt({ epoch: event.data.epoch, loggedOut: false });
        tokenStore.set(event.data.session);
        revision += 1;
        onRemoteSession?.();
      }
    };
  }

  const withLock = <T>(work: () => Promise<T>) => (locks ? locks.request(lockName, work) : work());

  return {
    async runRefresh(attempt) {
      const startingState = logoutFinality.read();
      if (startingState.loggedOut) return null;
      const startingRevision = revision;
      return withLock(async () => {
        const lockedState = logoutFinality.read();
        if (lockedState.loggedOut || lockedState.epoch !== startingState.epoch) return null;
        const synchronized = tokenStore.get();
        if (
          startingRevision !== revision &&
          synchronized?.accessToken &&
          !isExpired(synchronized.accessToken)
        ) {
          return synchronized;
        }
        const session = await attempt();
        const completedState = logoutFinality.read();
        if (completedState.loggedOut || completedState.epoch !== startingState.epoch) return null;
        publish(session, completedState.epoch);
        return session;
      });
    },
    async runLogout<T>(attempt: () => Promise<T>): Promise<T> {
      const state = logoutFinality.advance(true);
      tokenStore.clear();
      channel?.postMessage({ type: 'logout', epoch: state.epoch });
      return withLock(attempt);
    },
    publishLogin(session) {
      const state = logoutFinality.advance(false);
      publish(session, state.epoch);
    },
    hasFinalLogout: () => logoutFinality.read().loggedOut,
    close() {
      channel?.close();
    },
  };
}
